import { StyledSmartIdModal } from './style';
import { Input } from '../../Input';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import { Button } from '../../Button';
import { useIntl } from 'react-intl';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import {
  CartActionType,
  useCartDispatch,
} from '../../../public/context/CartContext';

interface SmartIdProps {
  open: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  fingerPrint: string;
}

interface WebsisResponse {
  id_code: string;
  session: string;
  age: number;
}

export const SmartIdModal = ({
  open,
  setIsOpen,
  fingerPrint,
}: SmartIdProps) => {
  const [confirmCode, setConfirmCode] = useState<string | null>(null);
  const [personalCode, setPersonalCode] = useState<string | null>(null);
  const [session, setSession] = useState<string | null>(null);
  const cartDispatch = useCartDispatch();
  const intl = useIntl();

  const [form] = Form.useForm();

  useEffect(() => {
    if (personalCode && session) {
      authenticate({
        session: session,
        country: 'lv',
        code: personalCode,
      });
    }
  }, [personalCode, session]);

  const { appendData: getCode, isLoading: code } = useQueryApiClient({
    request: {
      url: '/api/v2/customer-auth/get-code',
      method: 'POST',
      headers: { 'X-Cart-Fingerprint': fingerPrint },
    },
    onSuccess: (response: WebsisResponse) => handleWebsis(response),
    onError: () => handleCloseModal(),
  });

  const { appendData: authenticate, isLoading: auth } = useQueryApiClient({
    request: {
      url: '/api/v2/customer-auth/authenticate',
      method: 'POST',
      headers: { 'X-Cart-Fingerprint': fingerPrint },
    },
    onSuccess: (response: any) => handleAuthenticate(response),
    onError: () => handleCloseModal(),
  });

  const isLoading = auth || code;

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      setPersonalCode(values.code);

      getCode({ country: 'lv', code: values.code });
    } catch (e) {}
  };

  const handleWebsis = (response: WebsisResponse) => {
    if (response.age === 0) {
      handleCloseModal();
      return message.error(
        intl.formatMessage({ id: 'message.invalid_personal_code' })
      );
    }

    if (response.age < 18) {
      handleCloseModal();
      return message.error(intl.formatMessage({ id: 'message.under_age' }));
    }

    setConfirmCode(response.id_code);
    setSession(response.session);
  };

  const handleAuthenticate = (response: any) => {
    if (response.success === true) {
      cartDispatch({
        type: CartActionType.AUTHORIZE_CART,
      });
      handleCloseModal();
      message.success(
        intl.formatMessage({ id: 'message.smart_id_authenticated' })
      );
    }

    if (response.success === false) {
      message.error(intl.formatMessage({ id: 'message.smart_id_fail' }));
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setConfirmCode(null);
    setSession(null);
    setPersonalCode(null);
  };

  return (
    <StyledSmartIdModal open={open} onCancel={handleCloseModal} footer={null}>
      <Form form={form} onFinish={handleSubmit}>
        <Input
          name={'code'}
          validations={['required', 'personalCode']}
          placeholder={intl.formatMessage({ id: 'general.personal_code' })}
        />
        {confirmCode ? (
          <div className={'smart-id'}>
            <div className={'title'}>
              {intl.formatMessage({ id: 'message.smart_id_confirm_code' })}
            </div>
            <div className={'confirm-code'}>{confirmCode}</div>
          </div>
        ) : (
          <div className={'action'}>
            <Button
              label={intl.formatMessage({ id: 'general.authorize' })}
              htmlType={'submit'}
              type={'primary'}
              loading={isLoading}
            />
          </div>
        )}
      </Form>
    </StyledSmartIdModal>
  );
};
